"use client";

import type { ComponentProps } from "react";
import { format } from "date-fns";
import {
  CartesianGrid,
  Cell,
  Bar,
  BarChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { EconomicEvent } from "@/vybe-trader-client/economic-calendar/types";
import { parseEconomicValue } from "./parse-economic-value";
import { cn } from "@/lib/utils";

const FCST = "hsl(var(--chart-2))";
const ACT = "hsl(var(--primary))";
const UP = "hsl(var(--chart-2))";
const DOWN = "hsl(var(--destructive))";

function labelForEvent(e: EconomicEvent): string {
  try {
    return format(new Date(e.dateTimeISO), "MMM d, yy");
  } catch {
    return "";
  }
}

/** One row per release; nulls omit points for that series (Recharts connectNulls). */
function buildLevelRows(events: readonly EconomicEvent[]) {
  const sorted = [...events].sort(
    (a, b) =>
      new Date(a.dateTimeISO).getTime() - new Date(b.dateTimeISO).getTime()
  );
  return sorted.map((e) => {
    const f = parseEconomicValue(e.forecast);
    const a = parseEconomicValue(e.actual);
    return {
      label: labelForEvent(e),
      forecast: f === null ? null : f,
      actual: a === null ? null : a,
    };
  });
}

/** Change diff: sequential change in actual (current − previous parseable actual). */
function buildChangeDiffRows(events: readonly EconomicEvent[]) {
  const sorted = [...events].sort(
    (a, b) =>
      new Date(a.dateTimeISO).getTime() - new Date(b.dateTimeISO).getTime()
  );
  const out: { label: string; delta: number }[] = [];
  let prevActual: number | null = null;
  for (const e of sorted) {
    const a = parseEconomicValue(e.actual);
    if (a === null) continue;
    if (prevActual !== null) {
      out.push({
        label: labelForEvent(e),
        delta: a - prevActual,
      });
    }
    prevActual = a;
  }
  return out;
}

function formatY(v: number) {
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}m`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(1)}k`;
  return String(Number.isInteger(v) ? v : v.toFixed(2));
}

/** Tight domain from data min/max (not from 0) so small ranges aren’t flattened. */
function yDomainForecastActual(
  data: ReturnType<typeof buildLevelRows>
): [number, number] {
  const vals: number[] = [];
  for (const d of data) {
    if (d.forecast !== null && Number.isFinite(d.forecast)) {
      vals.push(d.forecast);
    }
    if (d.actual !== null && Number.isFinite(d.actual)) {
      vals.push(d.actual);
    }
  }
  if (vals.length === 0) return [0, 1];
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const span = hi - lo;
  const anchor = Math.max(Math.abs(lo), Math.abs(hi), Number.EPSILON);
  const pad =
    span > 0
      ? span * 0.08
      : Math.max(anchor * 0.06, 1e-6);
  return [lo - pad, hi + pad];
}

function yDomainDeltas(deltas: readonly number[]): [number, number] {
  if (deltas.length === 0) return [-1, 1];
  const lo = Math.min(...deltas);
  const hi = Math.max(...deltas);
  const span = hi - lo;
  const pad =
    span > 0
      ? span * 0.12
      : Math.max(Math.abs(lo), Math.abs(hi), 1e-9) * 0.15;
  return [lo - pad, hi + pad];
}

function LevelsChart({ data }: { data: ReturnType<typeof buildLevelRows> }) {
  const hasFc = data.some((d) => d.forecast !== null);
  const hasAc = data.some((d) => d.actual !== null);
  const yDomain = yDomainForecastActual(data);
  if (!hasFc && !hasAc) {
    return (
      <div className="flex h-[140px] flex-col rounded-md border border-border bg-muted/20 px-2 py-2">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Forecast & actual
        </span>
        <div className="flex flex-1 items-center justify-center text-[11px] text-muted-foreground">
          No parseable forecast or actual values
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1 rounded-md border border-border bg-muted/10 px-2 py-2">
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Forecast & actual
      </span>
      <div className="h-[140px] w-full min-h-0 min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 4, right: 8, bottom: 0, left: 4 }}
          >
            <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.5} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 9 }}
              stroke="hsl(var(--muted-foreground))"
              interval="preserveStartEnd"
            />
            <YAxis
              width={40}
              tick={{ fontSize: 9 }}
              stroke="hsl(var(--muted-foreground))"
              tickFormatter={formatY}
              domain={yDomain}
              allowDataOverflow
            />
            <Tooltip
              contentStyle={{
                background: "hsl(var(--popover))",
                border: "1px solid hsl(var(--border))",
                borderRadius: 6,
                fontSize: 11,
              }}
              formatter={
                ((value: number | null, name: string) => [
                  value === null ? "—" : formatY(value),
                  name === "forecast" ? "Forecast" : "Actual",
                ]) as ComponentProps<typeof Tooltip>["formatter"]
              }
              labelFormatter={(_, payload) =>
                payload?.[0]?.payload?.label ?? ""
              }
            />
            <Legend
              wrapperStyle={{ fontSize: 10 }}
              formatter={(value) =>
                value === "forecast"
                  ? "Forecast"
                  : value === "actual"
                    ? "Actual"
                    : value
              }
            />
            {hasFc ? (
              <Line
                type="monotone"
                dataKey="forecast"
                name="forecast"
                stroke={FCST}
                strokeWidth={1.5}
                dot={{ r: 2, fill: FCST }}
                connectNulls
                isAnimationActive={false}
              />
            ) : null}
            {hasAc ? (
              <Line
                type="monotone"
                dataKey="actual"
                name="actual"
                stroke={ACT}
                strokeWidth={1.5}
                dot={{ r: 2, fill: ACT }}
                connectNulls
                isAnimationActive={false}
              />
            ) : null}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ChangeDiffChart({
  rows,
}: {
  rows: { label: string; delta: number }[];
}) {
  if (rows.length === 0) {
    return (
      <div className="flex h-[140px] flex-col rounded-md border border-border bg-muted/20 px-2 py-2">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Change diff
        </span>
        <div className="flex flex-1 items-center justify-center px-2 text-center text-[11px] text-muted-foreground">
          Need two or more releases with parseable actuals.
        </div>
      </div>
    );
  }

  const data = rows.map((r) => ({
    ...r,
    fill: r.delta >= 0 ? UP : DOWN,
  }));
  const deltaDomain = yDomainDeltas(data.map((d) => d.delta));

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1 rounded-md border border-border bg-muted/10 px-2 py-2">
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Change diff (actual vs prior release)
      </span>
      <div className="h-[140px] w-full min-h-0 min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 4, right: 8, bottom: 2, left: 4 }}
          >
            <CartesianGrid stroke="hsl(var(--border))" strokeOpacity={0.5} />
            <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.6} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 9 }}
              stroke="hsl(var(--muted-foreground))"
              interval="preserveStartEnd"
            />
            <YAxis
              width={40}
              tick={{ fontSize: 9 }}
              stroke="hsl(var(--muted-foreground))"
              tickFormatter={formatY}
              domain={deltaDomain}
              allowDataOverflow
            />
            <Tooltip
              contentStyle={{
                background: "hsl(var(--popover))",
                border: "1px solid hsl(var(--border))",
                borderRadius: 6,
                fontSize: 11,
              }}
              formatter={
                ((v: number) => [formatY(v), "Δ actual"]) as ComponentProps<
                  typeof Tooltip
                >["formatter"]
              }
              labelFormatter={(_, payload) =>
                payload?.[0]?.payload?.label ?? ""
              }
            />
            <Bar dataKey="delta" radius={[3, 3, 0, 0]} maxBarSize={28}>
              {data.map((e, i) => (
                <Cell key={i} fill={e.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function EconomicSeriesCharts({
  occurrences,
  className,
}: {
  occurrences: readonly EconomicEvent[];
  className?: string;
}) {
  const levels = buildLevelRows(occurrences);
  const changeDiffRows = buildChangeDiffRows(occurrences);

  return (
    <div className={cn("flex min-h-0 flex-col gap-2", className)}>
      <LevelsChart data={levels} />
      <ChangeDiffChart rows={changeDiffRows} />
    </div>
  );
}
